"use client";

import { parseAdsAttribution } from "@teksta/shared-types";
import { deleteCookie, getCookie, setCookie } from "cookies-next/client";
import { paidTouchFromUrl } from "./ads-attribution";
import {
  ATTRIBUTION_COOKIE,
  ATTRIBUTION_MAX_AGE_MS,
  type AttributionStorage,
  captureAttributionStorage,
  type EntryLabels,
  entryLabels,
  mergeAttributionStorage,
  parseAttributionStorage,
  serializeAttributionStorage,
} from "./attribution-storage";
import { getConsentChoice } from "./consent-client";

export const ADS_ATTRIBUTION_COOKIE = ATTRIBUTION_COOKIE;
const LEGACY_ADS_COOKIE = "t_ads_attribution";
// Keep unresolved arrivals only in this document's memory, never browser storage.
let pendingAttribution: AttributionStorage = { v: 1 };
let lastCapturedSearch: string | undefined;

export function getAttributionStorage() {
  const record = parseAttributionStorage(getCookie(ATTRIBUTION_COOKIE));
  return getConsentChoice() === "necessary"
    ? { v: 1 as const, entry: record.entry?.source === "landing" ? record.entry : undefined }
    : record;
}

// Measurement is independent of optional browser persistence. Validate memory
// on read too: an open tab must not extend a click's original lifetime.
export function getMeasurementAttribution(): AttributionStorage {
  return mergeAttributionStorage(
    parseAttributionStorage(getCookie(ATTRIBUTION_COOKIE)),
    parseAttributionStorage(serializeAttributionStorage(pendingAttribution))
  );
}

export function captureAdsAttribution(labels?: EntryLabels) {
  const choice = getConsentChoice();
  const search = window.location.search;
  let record = getAttributionStorage();
  if (choice === "necessary") {
    // Withdrawal removes stored advertising data from the browser but not the
    // same-document measurement record: conversion measurement is
    // consent-independent (ROPA A5a). No advertising cookie is written here.
    pendingAttribution = mergeAttributionStorage(
      pendingAttribution,
      parseAttributionStorage(getCookie(ATTRIBUTION_COOKIE))
    );
    for (const name of [LEGACY_ADS_COOKIE, "t_gclid", "t_msclkid", "t_gbraid", "t_wbraid"]) {
      deleteCookie(name, { path: "/" });
    }
    if (search !== lastCapturedSearch) {
      pendingAttribution = captureAttributionStorage(pendingAttribution, search);
    }
  } else if (choice === "all") {
    record = mergeAttributionStorage(
      record,
      parseAttributionStorage(serializeAttributionStorage(pendingAttribution))
    );
    pendingAttribution = { v: 1 };
    // Acceptance and repeated effects are not new arrivals on an already observed URL.
    if (search !== lastCapturedSearch) record = captureAttributionStorage(record, search);
  } else if (search !== lastCapturedSearch) {
    pendingAttribution = captureAttributionStorage(pendingAttribution, search);
  }
  lastCapturedSearch = search;
  const params = new URLSearchParams(window.location.search);
  const entry = entryLabels(
    labels ?? {
      source: params.get("utm_source") ?? undefined,
      campaign: params.get("utm_campaign") || params.get("utm_kamp") || undefined,
      medium: params.get("utm_medium") ?? undefined,
    }
  );
  // Internal navigation labels are functional signup context, not advertising consent.
  // Explicit page labels took precedence over signup-link UTMs in the legacy helper.
  // Keep that behavior (notably songs_feature -> add-subs-to-songs routing).
  const internalEntry = entry?.source === "landing";
  const hasPageEntry =
    record.entry?.source === "landing" &&
    (record.entry.medium === "article" || record.entry.medium === "landing");
  if (
    entry &&
    ((labels && (internalEntry || choice === "all")) ||
      (internalEntry && !hasPageEntry) ||
      (!internalEntry && choice === "all" && !paidTouchFromUrl(window.location.search)))
  ) {
    record.entry = { ...entry, at: Date.now() };
  }
  const raw = serializeAttributionStorage(record);
  if (
    !record.entry &&
    !record.paid &&
    !record.gclid &&
    !record.msclkid &&
    !record.gbraid &&
    !record.wbraid
  ) {
    if (getCookie(ATTRIBUTION_COOKIE)) deleteCookie(ATTRIBUTION_COOKIE, { path: "/" });
    return;
  }
  if (!raw) return;
  // A saved paid touch supersedes legacy attribution, including after its expiry.
  if (record.paid && choice === "all" && getCookie(LEGACY_ADS_COOKIE)) {
    deleteCookie(LEGACY_ADS_COOKIE, { path: "/" });
  }
  if (raw === getCookie(ATTRIBUTION_COOKIE)) return;
  setCookie(ATTRIBUTION_COOKIE, raw, {
    path: "/",
    sameSite: "lax",
    secure: window.location.protocol === "https:",
    maxAge: ATTRIBUTION_MAX_AGE_MS / 1000,
  });
}

export function getAdsAttribution() {
  const record = getMeasurementAttribution();
  if (record.paid) return parseAdsAttribution(record.paid);
  // Read-only migration: never fabricate a timestamp or extend a legacy cookie's TTL.
  try {
    const raw = getCookie(LEGACY_ADS_COOKIE);
    if (!raw || raw.length > 3800) return;
    return parseAdsAttribution(JSON.parse(raw));
  } catch {
    return;
  }
}
